import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import app from "../../../../global-account/app.js";
import { toast, useB3Account } from "../../../../global-account/react/index.js";
import { ecosystemWalletId } from "../../../../shared/constants/index.js";
import { thirdwebB3Mainnet } from "../../../../shared/constants/chains/b3Chain.js";
import { client } from "../../../../shared/utils/thirdweb.js";
import { Loader2, Mail, Phone, WalletIcon } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { useLinkProfile, useProfiles } from "thirdweb/react";
import { createWallet, preAuthenticate } from "thirdweb/wallets";
import { WalletRow } from "../../index.js";
import { useModalStore } from "../../stores/useModalStore.js";
import { getProfileDisplayInfo } from "../../utils/profileDisplay.js";
import { AppleIcon } from "../icons/AppleIcon.js";
import { DiscordIcon } from "../icons/DiscordIcon.js";
import { FarcasterIcon } from "../icons/FarcasterIcon.js";
import { GoogleIcon } from "../icons/GoogleIcon.js";
import { XIcon } from "../icons/XIcon.js";
import ModalHeader from "../ModalHeader/ModalHeader.js";
import { Button } from "../ui/button.js";
const AUTH_METHODS = [{
  id: "email",
  label: "Email",
  enabled: true,
  icon: _jsx(Mail, {
    className: "text-b3-primary-blue size-6"
  })
}, {
  id: "phone",
  label: "Phone",
  enabled: true,
  icon: _jsx(Phone, {
    className: "text-b3-primary-blue size-6"
  })
}, {
  id: "google",
  label: "Google",
  enabled: true,
  icon: _jsx(GoogleIcon, {
    className: "size-6"
  })
}, {
  id: "x",
  label: "X (Twitter)",
  enabled: true,
  icon: _jsx(XIcon, {
    className: "size-6"
  })
}, {
  id: "discord",
  label: "Discord",
  enabled: true,
  icon: _jsx(DiscordIcon, {
    className: "size-6"
  })
}, {
  id: "apple",
  label: "Apple",
  enabled: true,
  icon: _jsx(AppleIcon, {
    className: "size-6"
  })
}, {
  id: "farcaster",
  label: "Farcaster",
  enabled: true,
  icon: _jsx(FarcasterIcon, {
    className: "size-6"
  })
}];
const WALLET_METHODS = [{
  id: "wallet",
  label: "Wallet",
  enabled: true,
  icon: _jsx(WalletIcon, {
    className: "size-6"
  }),
  walletType: "com.coinbase.wallet"
}, {
  id: "wallet",
  label: "Wallet",
  enabled: true,
  icon: _jsx(WalletIcon, {
    className: "size-6"
  }),
  walletType: "io.metamask"
}, {
  id: "wallet",
  label: "Wallet",
  enabled: true,
  icon: _jsx(WalletIcon, {
    className: "size-6"
  }),
  walletType: "me.rainbow"
}, {
  id: "wallet",
  label: "Wallet",
  enabled: true,
  icon: _jsx(WalletIcon, {
    className: "size-6"
  }),
  walletType: "app.phantom"
}, {
  id: "wallet",
  label: "Wallet",
  enabled: true,
  icon: _jsx(WalletIcon, {
    className: "size-6"
  }),
  walletType: "io.rabby"
}, {
  id: "wallet",
  label: "Wallet",
  enabled: true,
  icon: _jsx(WalletIcon, {
    className: "size-6"
  }),
  walletType: "walletConnect"
}];
export function LinkNewAccount({
  onSuccess: onSuccessCallback,
  onError,
  onClose,
  chain,
  partnerId,
  className
}) {
  const isLinking = useModalStore(state => state.isLinking);
  const linkingMethod = useModalStore(state => state.linkingMethod);
  const setLinkingState = useModalStore(state => state.setLinkingState);
  const navigateBack = useModalStore(state => state.navigateBack);
  const setB3ModalContentType = useModalStore(state => state.setB3ModalContentType);
  const [selectedMethod, setSelectedMethod] = useState(null);
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [otp, setOtp] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [error, setError] = useState(null);
  const {
    data: profilesRaw = []
  } = useProfiles({
    client
  });
  // Get connected auth methods
  const connectedAuthMethods = profilesRaw.filter(profile => !["custom_auth_endpoint"].includes(profile.type)).map(profile => profile.type);
  // Filter available auth methods
  const availableAuthMethods = AUTH_METHODS.filter(method => !connectedAuthMethods.includes(method.id) && method.enabled);
  const profiles = profilesRaw.filter(profile => !["custom_auth_endpoint"].includes(profile.type)).map(profile => ({
    ...getProfileDisplayInfo(profile),
    originalProfile: profile
  }));
  const account = useB3Account();
  const {
    mutate: linkProfile
  } = useLinkProfile();
  const onSuccess = useCallback(async () => {
    await onSuccessCallback?.();
  }, [onSuccessCallback]);
  // Reset linking state when component unmounts
  useEffect(() => {
    return () => {
      if (isLinking) {
        setLinkingState(false);
      }
    };
  }, [isLinking, setLinkingState]);
  const mutationOptions = {
    onError: error => {
      console.error("Error linking account:", error);
      toast.error(error.message);
      setLinkingState(false);
      onError?.(error);
    },
    onSuccess: async data => {
      console.log("Raw Link Account Data:", data);
      try {
        console.log("Sync user data...");
        await app.service("users").syncTwProfiles({});
      } catch (refreshError) {
        console.warn("⚠️ Could not sync user data:", refreshError);
      }
    }
  };
  const validateInput = () => {
    if (selectedMethod === "email") {
      if (!email) {
        setError("Please enter your email address");
        return false;
      }
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
        setError("Please enter a valid email address");
        return false;
      }
    } else if (selectedMethod === "phone") {
      if (!phone) {
        setError("Please enter your phone number");
        return false;
      }
      if (!/^\+?[\d\s-]{10,}$/.test(phone)) {
        setError("Please enter a valid phone number");
        return false;
      }
    }
    setError(null);
    return true;
  };
  const handleSendOTP = async () => {
    if (!validateInput()) return;
    try {
      setLinkingState(true, selectedMethod);
      setError(null);
      if (selectedMethod === "email") {
        await preAuthenticate({
          client,
          strategy: "email",
          email,
          ecosystem: {
            id: ecosystemWalletId,
            partnerId: partnerId
          }
        });
      } else if (selectedMethod === "phone") {
        await preAuthenticate({
          client,
          strategy: "phone",
          phoneNumber: phone,
          ecosystem: {
            id: ecosystemWalletId,
            partnerId: partnerId
          }
        });
      }
      setOtpSent(true);
    } catch (error) {
      console.error("Error sending OTP:", error);
      setError(error instanceof Error ? error.message : "Failed to send OTP");
      onError?.(error);
      setLinkingState(false);
    }
  };
  const handleLinkAccount = async () => {
    if (!otp) {
      console.error("No OTP entered");
      setError("Please enter the verification code");
      return;
    }
    try {
      setOtpSent(false);
      setLinkingState(true, selectedMethod);
      setError(null);
      if (selectedMethod === "email") {
        await linkProfile({
          client,
          strategy: "email",
          email,
          verificationCode: otp
        }, mutationOptions);
      } else if (selectedMethod === "phone") {
        await linkProfile({
          client,
          strategy: "phone",
          phoneNumber: phone,
          verificationCode: otp
        }, mutationOptions);
      }
    } catch (error) {
      console.error("Error linking account:", error);
      setError(error instanceof Error ? error.message : "Failed to link account");
      onError?.(error);
    }
  };
  const handleLinkWallet = async walletType => {
    setLinkingState(true, "wallet");
    console.log("selectedMethod", walletType);
    try {
      if (!walletType) {
        throw new Error("Wallet type not found");
      }
      await linkProfile({
        client,
        strategy: "wallet",
        wallet: createWallet(walletType),
        chain: thirdwebB3Mainnet
      }, mutationOptions);
    } catch (error) {
      console.error("Error linking account:", error);
      setError(error instanceof Error ? error.message : "Failed to link account");
      onError?.(error);
    }
  };
  const handleSocialLink = async strategy => {
    try {
      console.log("handleSocialLink", strategy);
      setLinkingState(true, strategy);
      setError(null);
      const result = await linkProfile({
        client,
        strategy
      }, mutationOptions);
      console.log("result", result);
      // Don't close the modal yet, wait for auth to complete
      onSuccess?.();
    } catch (error) {
      console.error("Error linking with social:", error);
      setError(error instanceof Error ? error.message : "Failed to link social account");
      onError?.(error);
      setLinkingState(false);
    }
  };
  // Add effect to handle social auth completion
  useEffect(() => {
    if (isLinking && linkingMethod && !selectedMethod) {
      // This means we're in a social auth flow
      const checkAuthStatus = async () => {
        try {
          // Wait a bit to ensure auth is complete
          await new Promise(resolve => setTimeout(resolve, 1000));
          onClose?.();
        } catch (error) {
          console.error("Error checking auth status:", error);
          setLinkingState(false);
        }
      };
      checkAuthStatus();
    }
  }, [isLinking, linkingMethod, selectedMethod, onClose, setLinkingState]);
  const handleFinishedLinking = useCallback(success => {
    if (success) {
      onSuccess?.();
      onClose?.();
    }
    setLinkingState(false);
    navigateBack();
    setB3ModalContentType({
      type: "manageAccount",
      activeTab: "settings",
      setActiveTab: () => {},
      chain,
      partnerId
    });
  }, [chain, navigateBack, partnerId, setB3ModalContentType, setLinkingState, onSuccess, onClose]);
  useEffect(() => {
    if (isLinking) {
      handleFinishedLinking(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profiles.length]);
  if (!account) {
    return _jsx("div", {
      className: "text-b3-foreground-muted py-8 text-center",
      children: "Please connect your account first"
    });
  }
  return _jsxs("div", {
    className: `b3-link-account space-y-6 ${className || ""}`,
    "data-testid": "link-account",
    children: [_jsx(ModalHeader, {
      title: "Link New Account"
    }), !selectedMethod ? _jsxs("div", {
      className: "b3-link-account-methods grid gap-3 px-5",
      children: [availableAuthMethods.map(method => _jsx(Button, {
        variant: "outline",
        className: "b3-link-account-method-button border-b3-line hover:border-b3-primary-blue/30 hover:bg-b3-primary-blue/5 text-b3-grey font-neue-montreal-medium h-14 justify-start bg-transparent px-6 text-base transition-all duration-200",
        "data-method": method.id,
        onClick: () => {
          if (method.id === "email" || method.id === "phone") {
            setSelectedMethod(method.id);
          } else {
            handleSocialLink(method.id);
          }
        },
        disabled: linkingMethod === method.id,
        children: isLinking && linkingMethod === method.id ? _jsx(Loader2, {
          className: "h-5 w-5 animate-spin"
        }) : _jsxs("div", {
          className: "b3-link-account-method-content flex items-center gap-4",
          children: [_jsx("div", {
            className: "b3-link-account-method-icon flex items-center justify-center rounded-full",
            children: method.icon
          }), _jsx("span", {
            className: "b3-link-account-method-label font-medium",
            children: method.label
          })]
        })
      }, method.id)), WALLET_METHODS.map(method => {
        if (!method.walletType) {
          return null;
        }
        return _jsx(WalletRow, {
          walletId: method.walletType,
          onClick: () => handleLinkWallet(method.walletType),
          isLoading: isLinking
        }, method.walletType);
      }), availableAuthMethods.length === 0 && _jsx("div", {
        className: "text-b3-foreground-muted py-8 text-center",
        children: "All available authentication methods have been connected"
      })]
    }) : _jsxs("div", {
      className: "b3-link-account-form space-y-4 px-5",
      children: [selectedMethod === "email" && _jsxs("div", {
        className: "space-y-2",
        children: [_jsx("label", {
          className: "text-b3-grey font-neue-montreal-medium text-sm",
          children: "Email Address"
        }), _jsx("input", {
          type: "email",
          placeholder: "Enter your email",
          className: "bg-b3-line text-b3-grey font-neue-montreal-medium focus:ring-b3-primary-blue/20 w-full rounded-xl p-4 focus:outline-none focus:ring-2",
          value: email,
          onChange: e => setEmail(e.target.value),
          disabled: otpSent || isLinking && linkingMethod === "email"
        })]
      }), selectedMethod === "phone" && _jsxs("div", {
        className: "space-y-2",
        children: [_jsx("label", {
          className: "text-b3-grey font-neue-montreal-medium text-sm",
          children: "Phone Number"
        }), _jsx("input", {
          type: "tel",
          placeholder: "Enter your phone number",
          className: "bg-b3-line text-b3-grey font-neue-montreal-medium focus:ring-b3-primary-blue/20 w-full rounded-xl p-4 focus:outline-none focus:ring-2",
          value: phone,
          onChange: e => setPhone(e.target.value),
          disabled: otpSent || isLinking && linkingMethod === "phone"
        }), _jsx("p", {
          className: "text-b3-foreground-muted font-neue-montreal-medium text-sm",
          children: "Include country code (e.g., +1 for US)"
        })]
      }), error && _jsx("div", {
        className: "text-b3-negative font-neue-montreal-medium py-2 text-sm",
        children: error
      }), (selectedMethod === "email" || selectedMethod === "phone") && (otpSent ? _jsxs("div", {
        className: "space-y-4",
        children: [_jsxs("div", {
          className: "space-y-2",
          children: [_jsx("label", {
            className: "text-b3-grey font-neue-montreal-medium text-sm",
            children: "Verification Code"
          }), _jsx("input", {
            type: "text",
            placeholder: "Enter verification code",
            className: "bg-b3-line text-b3-grey font-neue-montreal-medium focus:ring-b3-primary-blue/20 w-full rounded-xl p-4 focus:outline-none focus:ring-2",
            value: otp,
            onChange: e => setOtp(e.target.value)
          })]
        }), _jsx(Button, {
          className: "bg-b3-primary-blue hover:bg-b3-primary-blue/90 font-neue-montreal-semibold h-12 w-full text-white",
          onClick: handleLinkAccount,
          children: "Link Account"
        })]
      }) : _jsx(Button, {
        className: "bg-b3-primary-blue hover:bg-b3-primary-blue/90 font-neue-montreal-semibold h-12 w-full text-white",
        onClick: handleSendOTP,
        disabled: !email && !phone || isLinking && linkingMethod === selectedMethod,
        children: isLinking && linkingMethod === selectedMethod ? _jsx(Loader2, {
          className: "animate-spin"
        }) : "Send Verification Code"
      }))]
    })]
  });
}